/**
 * Owner Dashboard V3 - 간단한 알림 형태 대시보드
 * 만료임박 현황을 간단한 알림으로 표시
 *
 * 2026-10-08 대표 결정 — '미출석 N명' 제거. ('총무 업무 타임라인'도 뺐다가 같은 날 되살림 —
 *   현장에서 첫 화면에 있어야 한다는 의견, 대표 지시)
 *   미출석은 출석부(빨강)가 같은 일을 더 정확히 하고, 총무 업무는
 *   [직원 관리] → [직원 현황]에 같은 패널(StaffTaskNowPanel)이 그대로 있다.
 *   같은 날 '빠른 메뉴'(출석부·회원관리·입출입기록·알림톡발송)도 제거 — 왼쪽 메뉴와 중복.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import CreditAlertCard from './dashboard/CreditAlertCard';
import DashboardNoticeCard from './dashboard/DashboardNoticeCard';
import UnpaidBillsCard from './dashboard/UnpaidBillsCard';
import StaffTaskTimelineCard from './dashboard/StaffTaskTimelineCard';
import './OwnerDashboardV3.css';

const getApiUrl = (path: string) => {
  if (API_BASE_URL === '') {
    return path;
  }
  return `${API_BASE_URL}${path}`;
};

interface DashboardSummary {
  expiringCount: number;
}

const OwnerDashboardV3: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary>({
    expiringCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      // 만료 임박 학생 조회
      const expiringRes = await fetch(getApiUrl('/v1/owner/dashboard/student-alerts'), { headers });
      let expiringCount = 0;
      if (expiringRes.ok) {
        const expiringData = await expiringRes.json();
        console.log('[Dashboard] 만료 임박 응답:', expiringData);
        const expiring = expiringData.data?.expiring_soon || [];
        expiringCount = expiring.length;
      } else {
        console.error('[Dashboard] 만료 임박 조회 실패:', expiringRes.status, expiringRes.statusText);
      }

      setSummary({ expiringCount });
    } catch (error) {
      console.error('대시보드 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short'
  });

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>대시보드를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="owner-dashboard-v3 simplified">
      {/* 본부 공지 */}
      <DashboardNoticeCard />

      {/* 크레딧 경고 */}
      <CreditAlertCard />

      {/* 입금하실 금액 — 알림톡 사용료 · 월 사용료 중 안내했는데 아직 입금 안 된 것 (2026-10-08) */}
      <UnpaidBillsCard />

      {/* 헤더 */}
      <div className="dashboard-header-simple">
        <h1>📋 오늘의 현황</h1>
        <span className="dashboard-date">{today}</span>
      </div>

      {/* 알림 목록 */}
      <div className="dashboard-alerts">
        {/* 만료 임박 알림 */}
        <div
          className={`alert-item ${summary.expiringCount > 0 ? 'info' : 'success'}`}
          onClick={() => navigate('/owner/members')}
        >
          <div className="alert-icon">
            {summary.expiringCount > 0 ? '⏰' : '✅'}
          </div>
          <div className="alert-content">
            <div className="alert-title">
              {summary.expiringCount > 0
                ? `이용권 만료 임박 ${summary.expiringCount}명`
                : '만료 임박 학생 없음'
              }
            </div>
          </div>
          <div className="alert-arrow">›</div>
        </div>
      </div>

      {/* 총무 업무 타임라인 — 2026-10-08 대표 지시로 되살림 */}
      <StaffTaskTimelineCard />
    </div>
  );
};

export default OwnerDashboardV3;
